import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  MessageSquare, Clock, HelpCircle, BarChart3, PieChart, Mail,
  Loader2, RefreshCw, Send,
} from 'lucide-react';
import { MetricCard } from '@/components/admin/shared/MetricCard';

const STATUS_ROWS = [
  { key: 'open',         label: 'Open',        color: 'bg-amber-500' },
  { key: 'in_progress',  label: 'In Progress', color: 'bg-blue-500' },
  { key: 'resolved',     label: 'Resolved',    color: 'bg-emerald-500' },
];

export const AdminSupportTab = ({
  // support tickets
  supportStats, supportTickets, supportFilter, supportLoading,
  selectedTicket, ticketResponse, updatingTicket,
  setSupportFilter, setSelectedTicket, setTicketResponse,
  loadSupportData, handleUpdateTicket,
  // contact submissions
  contactSubmissions, contactLoading, selectedContact, contactReply, sendingContactReply,
  setSelectedContact, setContactReply,
  loadContactSubmissions, handleUpdateContactStatus, handleContactReply,
}) => {
  return (
    <>
      <div className="space-y-6" data-testid="admin-support-tab">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="support-stats-grid">
          <MetricCard label="Total Tickets" value={supportStats?.total || 0} icon={MessageSquare} color="blue" />
          <MetricCard
            label="Open Tickets"
            value={supportStats?.by_status?.open || 0}
            icon={Clock}
            color="amber"
            trend={supportStats?.by_status?.open > 0 ? 'Needs attention' : null}
          />
          <MetricCard label="Support Requests" value={supportStats?.by_type?.support || 0} icon={HelpCircle} color="purple" />
          <MetricCard label="Feedback Items" value={supportStats?.by_type?.feedback || 0} icon={MessageSquare} color="emerald" />
        </div>

        {/* Charts/Analytics Row */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Status Distribution */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-blue-600" />
                Status Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!supportStats ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : (
                <div className="space-y-3">
                  {STATUS_ROWS.map((row) => {
                    const value = supportStats?.by_status?.[row.key] || 0;
                    const total = supportStats?.total || 1;
                    const percent = Math.round((value / total) * 100) || 0;
                    return (
                      <div key={row.key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">{row.label}</span>
                          <span className="text-slate-900 font-medium">{value} ({percent}%)</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${row.color} transition-all`} style={{ width: `${percent}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category Distribution */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                Top Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!supportStats ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : supportStats?.by_category?.length > 0 ? (
                <div className="space-y-2">
                  {supportStats.by_category.slice(0, 6).map((cat) => (
                    <div key={`cat-${cat.category || 'other'}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-700">{cat.category || 'Other'}</span>
                      <Badge variant="outline" className="border-slate-300 text-slate-600">{cat.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">No categories yet</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tickets List */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                Support Tickets & Feedback
              </CardTitle>
              <CardDescription className="text-slate-600">View and manage all user submissions</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Select value={supportFilter.type} onValueChange={(v) => setSupportFilter(f => ({ ...f, type: v }))}>
                <SelectTrigger className="w-[130px] bg-white border-slate-300 text-slate-700" data-testid="support-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="feedback">Feedback</SelectItem>
                </SelectContent>
              </Select>
              <Select value={supportFilter.status} onValueChange={(v) => setSupportFilter(f => ({ ...f, status: v }))}>
                <SelectTrigger className="w-[130px] bg-white border-slate-300 text-slate-700"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadSupportData} disabled={supportLoading} className="bg-blue-600 hover:bg-blue-700">
                {supportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 text-sm text-slate-600 font-medium">Ticket</th>
                    <th className="text-left p-3 text-sm text-slate-600 font-medium">User</th>
                    <th className="text-left p-3 text-sm text-slate-600 font-medium">Type</th>
                    <th className="text-left p-3 text-sm text-slate-600 font-medium">Priority</th>
                    <th className="text-left p-3 text-sm text-slate-600 font-medium">Status</th>
                    <th className="text-left p-3 text-sm text-slate-600 font-medium">Date</th>
                    <th className="text-right p-3 text-sm text-slate-600 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {supportLoading ? (
                    <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></td></tr>
                  ) : supportTickets.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-slate-500" data-testid="support-tickets-empty">No tickets found</td></tr>
                  ) : supportTickets.map((ticket) => (
                    <tr key={ticket.ticket_id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`support-ticket-row-${ticket.ticket_id}`}>
                      <td className="p-3">
                        <p className="text-slate-900 font-medium text-sm">{ticket.subject}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[200px]">{ticket.description}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-slate-900 text-sm">{ticket.user_name}</p>
                        <p className="text-xs text-slate-500">{ticket.user_email}</p>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className={ticket.type === 'support' ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-purple-300 text-purple-700 bg-purple-50'}>
                          {ticket.type}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge className={
                          ticket.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                          ticket.priority === 'high' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {ticket.priority}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge className={
                          ticket.status === 'open' ? 'bg-amber-100 text-amber-700' :
                          ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          ticket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {ticket.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm text-slate-600">{new Date(ticket.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedTicket(ticket)}
                          className="border-slate-300 text-slate-600 hover:bg-slate-100"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Contact Form Submissions */}
        <Card className="bg-white border-slate-200 shadow-sm mt-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-600" />
                Contact Form Submissions
              </CardTitle>
              <CardDescription className="text-slate-600">
                Messages from the website contact form
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadContactSubmissions} disabled={contactLoading} className="border-slate-300 text-slate-700">
              {contactLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </CardHeader>
          <CardContent>
            {contactLoading && contactSubmissions.length === 0 ? (
              <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 text-emerald-600 animate-spin" /></div>
            ) : contactSubmissions.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-slate-300 rounded-lg">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No contact submissions yet</p>
                <Button variant="outline" size="sm" onClick={loadContactSubmissions} className="mt-4 border-slate-300 text-slate-600">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Load Submissions
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {contactSubmissions.map((contact) => (
                  <div
                    key={contact.submission_id}
                    data-testid={`contact-submission-${contact.submission_id}`}
                    className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                      contact.status === 'new'
                        ? 'border-blue-300 bg-blue-50/50 hover:bg-blue-50'
                        : contact.status === 'replied'
                        ? 'border-emerald-200 bg-emerald-50/30 hover:bg-emerald-50/50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => { setSelectedContact(contact); handleUpdateContactStatus(contact.submission_id, 'read'); }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-slate-900 truncate">{contact.subject}</h4>
                          {contact.status === 'new' && (<Badge className="bg-blue-100 text-blue-700">New</Badge>)}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          <span className="font-medium">{contact.name}</span> • {contact.email}
                          {contact.company && <span className="text-slate-400"> • {contact.company}</span>}
                        </p>
                        <p className="text-sm text-slate-500 line-clamp-2">{contact.message}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Badge className={
                          contact.status === 'new' ? 'bg-blue-100 text-blue-700' :
                          contact.status === 'read' ? 'bg-amber-100 text-amber-700' :
                          contact.status === 'replied' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {contact.status}
                        </Badge>
                        <span className="text-xs text-slate-400">{new Date(contact.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contact Reply Dialog */}
      <Dialog open={!!selectedContact} onOpenChange={() => { setSelectedContact(null); setContactReply(''); }}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              Contact Submission
            </DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-slate-500">Name</Label><p className="text-slate-900 font-medium">{selectedContact.name}</p></div>
                <div><Label className="text-slate-500">Email</Label><p className="text-slate-900">{selectedContact.email}</p></div>
                {selectedContact.company && (
                  <div><Label className="text-slate-500">Company</Label><p className="text-slate-700">{selectedContact.company}</p></div>
                )}
                <div><Label className="text-slate-500">Submitted</Label><p className="text-slate-700">{new Date(selectedContact.created_at).toLocaleString()}</p></div>
              </div>

              <div><Label className="text-slate-500">Subject</Label><p className="text-slate-900 font-medium">{selectedContact.subject}</p></div>

              <div>
                <Label className="text-slate-500">Message</Label>
                <div className="mt-1 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>

              {selectedContact.status === 'replied' && selectedContact.reply_message && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <Label className="text-emerald-700">Previous Reply</Label>
                  <p className="text-emerald-800 mt-1">{selectedContact.reply_message}</p>
                  <p className="text-xs text-emerald-600 mt-2">Replied on {new Date(selectedContact.replied_at).toLocaleString()}</p>
                </div>
              )}

              <div className="border-t border-slate-200 pt-4">
                <Label className="text-slate-700 mb-2 block">Send Reply Email</Label>
                <Textarea
                  value={contactReply}
                  onChange={(e) => setContactReply(e.target.value)}
                  placeholder="Type your reply here. This will be sent directly to the customer's email..."
                  className="bg-white border-slate-300 text-slate-900 resize-none"
                  rows={5}
                  data-testid="contact-reply-textarea"
                />
                <p className="text-xs text-slate-500 mt-1">Requires active SMTP configuration</p>
              </div>

              <div className="flex justify-between gap-3 pt-2">
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleUpdateContactStatus(selectedContact.submission_id, 'archived')}
                    className="border-slate-300 text-slate-600"
                  >
                    Archive
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setSelectedContact(null); setContactReply(''); }}
                    className="border-slate-300 text-slate-700"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={handleContactReply}
                    disabled={!contactReply.trim() || sendingContactReply}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="contact-reply-send-btn"
                  >
                    {sendingContactReply ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Reply
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">Ticket Details</DialogTitle></DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-slate-500">Subject</Label><p className="text-slate-900 font-medium">{selectedTicket.subject}</p></div>
                <div><Label className="text-slate-500">Submitted By</Label><p className="text-slate-900">{selectedTicket.user_name} ({selectedTicket.user_email})</p></div>
                <div><Label className="text-slate-500">Type</Label><Badge variant="outline" className={selectedTicket.type === 'support' ? 'border-blue-300 text-blue-700' : 'border-purple-300 text-purple-700'}>{selectedTicket.type}</Badge></div>
                <div><Label className="text-slate-500">Category</Label><p className="text-slate-700">{selectedTicket.category || selectedTicket.issue_type || 'General'}</p></div>
              </div>

              <div>
                <Label className="text-slate-500">Description</Label>
                <div className="p-3 bg-slate-50 rounded-lg mt-1 text-slate-700 text-sm whitespace-pre-wrap">{selectedTicket.description}</div>
              </div>

              {selectedTicket.attachments?.length > 0 && (
                <div>
                  <Label className="text-slate-500">Attachments</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedTicket.attachments.map((att, i) => (
                      <Badge key={`${selectedTicket.ticket_id}-att-${i}`} variant="outline" className="border-slate-300">{att.file_name}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedTicket.admin_response && (
                <div>
                  <Label className="text-slate-500">Previous Response</Label>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-1 text-slate-700 text-sm">{selectedTicket.admin_response}</div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Update Status</Label>
                  <Select value={selectedTicket.status} onValueChange={(v) => handleUpdateTicket(selectedTicket.ticket_id, { status: v })}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-500">Update Priority</Label>
                  <Select value={selectedTicket.priority} onValueChange={(v) => handleUpdateTicket(selectedTicket.ticket_id, { priority: v })}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-slate-500">Send Response to User</Label>
                <Textarea
                  value={ticketResponse}
                  onChange={(e) => setTicketResponse(e.target.value)}
                  placeholder="Type your response to the user..."
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 mt-1"
                  rows={4}
                />
                <Button
                  onClick={() => handleUpdateTicket(selectedTicket.ticket_id, { admin_response: ticketResponse, status: 'in_progress' })}
                  disabled={!ticketResponse.trim() || updatingTicket}
                  className="mt-2 bg-blue-600 hover:bg-blue-700"
                >
                  {updatingTicket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Response
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminSupportTab;
